/**
 * `link` — `LinkValue` (`specs/M2-tier2-column-types.md` §5.1, `[09§A.7 link]`, `[cvs§2 link]`).
 *
 * The busiest tier-2 type on this account: **5,602 populated cells** in 13,822 samples
 * `[snap]`. Stored `value` is `{"url":"…","text":"…","changed_at":…}` — note the write key is
 * `text` while the read fragment calls the same thing `url_text` `[09§A.7 rule 3]`.
 *
 * **`toText` is two-shaped and both shapes are measured** in the 2026-09-10 snapshot: a cell
 * whose display text equals the url renders as the bare url, and a cell with its own display
 * text renders as `"Amazon - https://www.amazon.com/…"` — i.e. `"<text> - <url>"`, text first.
 * (`discovery/column-value-shapes.md` calls it *"url - display text"*; the raw cells say the
 * opposite order, and the raw cells win.)
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const linkValueSchema = z.looseObject({
    url: z.string(),
    /** The display text. Equal to the url when the user typed none `[cvs§2 link]`. */
    text: z.string().optional(),
    changed_at: z.string().nullable().optional(),
});
import { linkFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** `settings` is `{}` on every link column in the account `[cvs§2 link]`; doc adds three keys. */
export const linkSettingsSchema = z.looseObject({
    defaultText: z.string().optional(),
    disableAutoTitle: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
/**
 * `change_simple_column_value`: `"https://monday.com Go to monday!"` — the url, a space, then
 * the rest as display text `[09§A.7 link]`.
 */
function fromSimple(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const space = trimmed.indexOf(' ');
    if (space < 0)
        return { url: trimmed, text: trimmed };
    return { url: trimmed.slice(0, space), text: trimmed.slice(space + 1).trim() };
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return fromSimple(raw);
    if (typeof raw !== 'object') {
        throw new ColumnValueError('link', `expected an object or string, received ${typeof raw}`);
    }
    const record = raw;
    const url = record.url;
    if (url === null || url === undefined || url === '')
        return null;
    if (typeof url !== 'string')
        throw new ColumnValueError('link', '"url" must be a string');
    const value = { url };
    // `url_text` is the read spelling of the same field; accept both so an exported cell
    // round-trips through a write `[09§A.7 rule 3]`.
    const text = typeof record.text === 'string' ? record.text : record.url_text;
    value.text = typeof text === 'string' && text !== '' ? text : url;
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
/** The rendering monday puts in `ColumnValue.text` and the CSV export. */
function renderText(value) {
    if (value === null)
        return '';
    const display = value.text ?? '';
    return display === '' || display === value.url ? value.url : `${display} - ${value.url}`;
}
export const linkColumn = defineColumnType({
    type: 'link',
    title: 'Link',
    description: 'A URL with an optional display text.',
    apiFragmentName: 'LinkValue',
    legacyTypeName: 'link',
    idPrefix: 'link',
    valueSchema: linkValueSchema,
    settingsSchema: linkSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: linkSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    /**
     * Verbatim: *"The `any_of`, `not_any_of`, `contains_text`, and `not_contains_text` operators
     * match against the link's **display text**, not the URL."* `[09§A.7 link]` — which is what
     * the `text_value` shadow holds, so the default text SQL is already right.
     */
    filterConditions: linkFilterConditions,
    filterOperators: operatorsOf(linkFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    parseSimpleValue(raw) {
        return raw === null ? null : fromSimple(raw);
    },
    toText(value) {
        return renderText(value);
    },
    toSortKeys(value) {
        return { text: textShadow(renderText(value)), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.url === b.url && (a.text ?? a.url) === (b.text ?? b.url);
    },
    toActivityValue(value) {
        return value === null ? null : { url: value.url, text: value.text ?? value.url };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { url: value.url, text: value.text ?? value.url };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        if (value === null)
            return '';
        const display = value.text ?? '';
        return display === '' || display === value.url ? value.url : `${value.url} ${display}`;
    },
    fromMondaySettings(raw) {
        return linkSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        // `url_text` equals the url when no display text was typed `[cvs§2 link]`.
        return { url: value.url, url_text: value.text ?? value.url, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    /** No webhook recording: the conservative pass-through is monday's own `value` (q330). */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet by the **display text**, which is what monday's own filter compares `[09§A.7 link]`. */
    bucketsOf(value) {
        if (value === null)
            return [];
        const display = value.text ?? value.url;
        return display === '' ? [] : [{ key: display, label: display }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map